// src/routes/PrivateRoute.tsx
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchAuthSession, fetchUserAttributes, signOut } from 'aws-amplify/auth';
import AppLayout from '../components/AppLayout';
import Loader from '../components/Loader';
import { decodeJwt, userStore } from '../features/auth';

const PrivateRoute: React.FC = () => {
  const location = useLocation();
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const clearAuthStorage = () => {
    const localStorageKeys = ['user', 'profile', 'returnUrl'];
    const sessionStorageKeys = ['access_token', 'id_token', 'refresh_token', 'expires_at', 'session_state', 'token'];

    localStorageKeys.forEach(key => localStorage.removeItem(key));
    sessionStorageKeys.forEach(key => sessionStorage.removeItem(key));
  };

  useEffect(() => {
    const validateSession = async () => {
      try {
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken?.toString();
        const idToken = session.tokens?.idToken?.toString();

        if (!accessToken) {
          setIsAuthenticated(false);
          return;
        }

        sessionStorage.setItem('access_token', accessToken);
        if (idToken) {
          sessionStorage.setItem('id_token', idToken);
        }
        userStore.setAuthToken(accessToken);

        const accessClaims = decodeJwt(accessToken) || {};
        const idClaims = idToken ? (decodeJwt(idToken) || {}) : {};

        // Federated (SSO) sign-ins already carry email/name claims in the ID
        // token from the upstream IdP via Cognito. Calling Cognito's GetUser
        // API (fetchUserAttributes) here too would be a redundant, unwanted
        // Cognito authentication API call for SSO users on every protected
        // route load - only fall back to it for native Cognito
        // (username/password) users, whose ID token may lack custom attributes.
        const isFederatedSignIn = !!idClaims.identities?.length;
        const attributes = isFederatedSignIn ? {} : ((await fetchUserAttributes().catch(() => ({}))) as Record<string, string>);

        const userEmail =
          idClaims.email ||
          idClaims.preferred_username ||
          attributes.email ||
          attributes.preferred_username ||
          '';

        const userFullName =
          idClaims.name ||
          idClaims['custom:fullname'] ||
          idClaims['custom:name'] ||
          accessClaims.fullname ||
          userEmail;

        const isExternal = isFederatedSignIn || accessClaims.username?.includes('_');

        userStore.setUser({
          email: userEmail,
          fullname: userFullName,
          isExternal,
        });
        setIsAuthenticated(true);
      } catch {
        setIsAuthenticated(false);
      } finally {
        setCheckingAuth(false);
      }
    };
    validateSession();
  }, []);

  useEffect(() => {
    if (!checkingAuth && !isAuthenticated) {
      localStorage.setItem('returnUrl', location.pathname);
      window.location.replace('/');
    }
  }, [checkingAuth, isAuthenticated, location.pathname]);

  if (checkingAuth || loggingOut) {
    return <Loader tip={loggingOut ? 'Signing out...' : 'Loading...'} />;
  }

  if (!isAuthenticated) {
    return <Loader />;
  }

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      clearAuthStorage();
      await signOut();
      window.location.replace('/');
    } catch (error) {
      console.error('Logout failed:', error);
      clearAuthStorage();
      setLoggingOut(false);
      window.location.replace('/');
    }
  };

  return <AppLayout onLogout={handleLogout} />;
};

export default PrivateRoute;
